export interface OffThreadTimerHandle {
  readonly id: number
}

interface PendingTimer {
  callback: () => void
  dueAtMs: number
}

/**
 * setTimeout that keeps its schedule in a background tab. Timers run on a
 * worker thread (see offThreadTimer.worker.ts); when no Worker is available,
 * or the worker fails, timers fall back to the page thread so callers always
 * get their callback.
 */
export class OffThreadTimer {
  // undefined = not tried yet, null = unavailable or failed
  private _worker: Worker | null | undefined = undefined
  private _nextId = 1
  private _pending = new Map<number, PendingTimer>()
  private _fallbacks = new Map<number, ReturnType<typeof setTimeout>>()

  public set(callback: () => void, delayMs: number): OffThreadTimerHandle {
    const id = this._nextId++
    const worker = this.getWorker()
    if (worker) {
      this._pending.set(id, { callback, dueAtMs: Date.now() + delayMs })
      worker.postMessage({ id, delayMs })
    } else {
      this.armOnPageThread(id, callback, delayMs)
    }
    return { id }
  }

  public clear(handle: OffThreadTimerHandle | null | undefined): void {
    if (!handle) {
      return
    }
    this._pending.delete(handle.id)
    const fallback = this._fallbacks.get(handle.id)
    if (fallback) {
      clearTimeout(fallback)
      this._fallbacks.delete(handle.id)
    }
  }

  public usesWorker(): boolean {
    return !!this.getWorker()
  }

  private armOnPageThread(id: number, callback: () => void, delayMs: number) {
    this._fallbacks.set(
      id,
      setTimeout(() => {
        this._fallbacks.delete(id)
        callback()
      }, delayMs),
    )
  }

  private getWorker(): Worker | null {
    if (this._worker !== undefined) {
      return this._worker
    }
    if (typeof Worker === 'undefined') {
      this._worker = null
      return null
    }
    try {
      const worker = new Worker(
        new URL('./offThreadTimer.worker.ts', import.meta.url),
        { type: 'module' },
      )
      worker.onmessage = ({ data }: MessageEvent<number>) => {
        const pending = this._pending.get(data)
        if (pending) {
          this._pending.delete(data)
          pending.callback()
        }
      }
      worker.onerror = () => this.degradeToPageThread()
      this._worker = worker
    } catch {
      this._worker = null
    }
    return this._worker
  }

  // A worker that fails to load never delivers; re-arm what it owed on the
  // page thread and stop using it.
  private degradeToPageThread() {
    this._worker?.terminate()
    this._worker = null
    const now = Date.now()
    for (const [id, { callback, dueAtMs }] of this._pending) {
      this.armOnPageThread(id, callback, Math.max(0, dueAtMs - now))
    }
    this._pending.clear()
  }
}
